import type { Theme, SxProps, CSSObject, Breakpoint } from '@mui/material/styles';

import { varAlpha, mergeClasses } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import { Logo as LogoBase } from 'src/components/logo';
import { Scrollbar as ScrollbarBase } from 'src/components/scrollbar';
import {
  NavSectionMini as NavSectionMiniBase,
  NavSectionVertical as NavSectionVerticalBase,
} from 'src/components/nav-section';

import { layoutClasses } from '../core/classes';
import { NavUpgrade } from '../components/nav-upgrade';
import { NavToggleButton } from '../components/nav-toggle-button';

// ----------------------------------------------------------------------

// Logo·Scrollbar·NavSection* 은 아직 JS 라 props 가 전부 필수로 추론된다 — 실제 계약(전부 선택)으로 좁혀 쓴다 (로직 무변)
const Logo = LogoBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  href?: string;
  isSingle?: boolean;
  disabled?: boolean;
  className?: string;
}>;

const Scrollbar = ScrollbarBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  fillContent?: boolean;
  children?: React.ReactNode;
  className?: string;
}>;

type NavSectionProps = {
  data?: unknown;
  sx?: SxProps<Theme>;
  cssVars?: CSSObject;
  slotProps?: Record<string, unknown>;
  checkPermissions?: (allowedRoles?: any) => boolean;
  enabledRootRedirect?: boolean;
};

const NavSectionMini = NavSectionMiniBase as unknown as React.ComponentType<NavSectionProps>;
const NavSectionVertical = NavSectionVerticalBase as unknown as React.ComponentType<NavSectionProps>;

type NavVerticalProps = React.ComponentProps<'div'> & {
  sx?: SxProps<Theme>;
  data?: unknown;
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  cssVars?: CSSObject;
  isNavMini?: boolean;
  onToggleNav?: () => void;
  checkPermissions?: (allowedRoles?: any) => boolean;
  layoutQuery?: Breakpoint;
};

export function NavVertical({
  sx,
  data,
  slots,
  cssVars,
  className,
  isNavMini,
  onToggleNav,
  checkPermissions,
  layoutQuery = 'md',
  ...other
}: NavVerticalProps) {
  const renderNavVertical = () => (
    <>
      {slots?.topArea ?? (
        <Box sx={{ pl: 3.5, pt: 2.5, pb: 1 }}>
          <Logo isSingle={false}/>
        </Box>
      )}

      <Scrollbar fillContent>
        {/*
          🚨 **하위를 단 줄도 링크로 둔다**(`enabledRootRedirect`). 기본값은 「하위가 있으면
            `div` 로 바꾼다」라서, 「추적 관리」 밑에 만든 화면을 달면 **추적 관리로 못 가게**
            된다 — 키워드를 등록하고 화면을 만들기 시작하는 자리가 그 화면이다.
          ⚠️ 「타겟 멘션 추적」도 같이 링크가 된다 — 제 라우트가 있는 줄이라 맞는 동작이다
            (그전에는 펴기만 되고 눌러도 안 갔다).
        */}
        <NavSectionVertical
          data={data}
          cssVars={cssVars}
          checkPermissions={checkPermissions}
          enabledRootRedirect
          sx={{ px: 2, flex: '1 1 auto' }}
        />
      </Scrollbar>

      {/* NavUpgrade를 항상 아래에 위치 */}
      {slots?.bottomArea ?? <NavUpgrade />}
    </>
  );

  const renderNavMini = () => (
    <>
      {slots?.topArea ?? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}>
          <Logo />
        </Box>
      )}

      <NavSectionMini
        data={data}
        cssVars={cssVars}
        checkPermissions={checkPermissions}
        sx={[
          (theme) => ({
            ...theme.mixins.hideScrollY,
            pb: 2,
            px: 0.5,
            flex: '1 1 auto',
            overflowY: 'auto',
          }),
        ]}
      />

      {slots?.bottomArea}
    </>
  );

  return (
    <NavRoot
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      className={mergeClasses([layoutClasses.nav.root, layoutClasses.nav.vertical, className])}
      sx={sx}
      {...other}
    >
      {/* ⚠️ 접는 단추는 **`onToggleNav` 를 준 화면에만** 선다 — 안 주면 접을 길이 없다 */}
      {onToggleNav && (
        <NavToggleButton
          isNavMini={isNavMini}
          onClick={onToggleNav}
          sx={[
            (theme) => ({
              display: 'none',
              [theme.breakpoints.up(layoutQuery)]: { display: 'inline-flex' },
            }),
          ]}
        />
      )}
      {isNavMini ? renderNavMini() : renderNavVertical()}
    </NavRoot>
  );
}

// ----------------------------------------------------------------------

const NavRoot = styled('div', {
  shouldForwardProp: (prop: string) => !['isNavMini', 'layoutQuery', 'sx'].includes(prop),
})<{ isNavMini?: boolean; layoutQuery?: Breakpoint }>(({ isNavMini, layoutQuery = 'md', theme }) => ({
  top: 0,
  left: 0,
  height: '100%',
  display: 'none',
  position: 'fixed',
  flexDirection: 'column',
  zIndex: 'var(--layout-nav-zIndex)',
  backgroundColor: 'var(--layout-nav-bg)',
  width: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
  borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)})`,
  transition: theme.transitions.create(['width'], {
    easing: 'var(--layout-transition-easing)',
    duration: 'var(--layout-transition-duration)',
  }),
  [theme.breakpoints.up(layoutQuery)]: { display: 'flex' },
}));
